import { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import AppShell from '../components/layout/AppShell';
import AppFooter from '../components/layout/AppFooter';
import TopNav from '../components/layout/TopNav';
import SetupCardModal from '../components/SetupCardModal';

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";
const TOPUP_AMOUNTS = [50, 75, 100];

export default function PaymentMethods({ onLogout, onNavigate }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [activeTab, setActiveTab] = useState('wallet');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [autoTopup, setAutoTopup] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  async function fetchAll() {
    try {
      const [walletRes, methodsRes] = await Promise.all([
        axios.get(`${API}/wallet`, { headers }),
        axios.get(`${API}/wallet/payment-methods`, { headers })
      ]);
      setWallet(walletRes.data);
      setTransactions(walletRes.data.transactions);
      setAutoTopup(walletRes.data.auto_topup);
      setPaymentMethods(methodsRes.data);
    } catch {
      showMessage('Erreur lors du chargement', 'error');
    }
  }

  function showMessage(text, type = 'success') {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  }

  async function handleDeleteMethod(id) {
    try {
      await axios.delete(`${API}/wallet/payment-methods/${id}`, { headers });
      showMessage('Moyen de paiement supprimé');
      fetchAll();
    } catch {
      showMessage('Erreur suppression', 'error');
    }
  }

  async function handleSetDefault(id) {
    try {
      await axios.put(`${API}/wallet/payment-methods/${id}/default`, {}, { headers });
      showMessage('Moyen de paiement par défaut mis à jour');
      fetchAll();
    } catch {
      showMessage('Erreur', 'error');
    }
  }

  async function handleAutoTopupToggle() {
    if (!autoTopup) return;
    try {
      await axios.put(`${API}/wallet/auto-topup`, { ...autoTopup, enabled: !autoTopup.enabled }, { headers });
      fetchAll();
    } catch {
      showMessage('Erreur', 'error');
    }
  }

  function getMethodIcon(type) {
    if (type === 'paypal') return '🅿️';
    if (type === 'paylib') return '📱';
    return '💳';
  }

  const tabs = [
    { key: 'wallet', label: 'Recharger' },
    { key: 'methods', label: 'Mes moyens de paiement' },
    { key: 'history', label: 'Historique' }
  ];

  return (
    <AppShell header={<TopNav isLoggedIn={true} onLogout={onLogout} onNavigate={onNavigate} />} footer={<AppFooter onNavigate={onNavigate} />} onNavigate={onNavigate}>      <div style={{ maxWidth: 700, margin: '0 auto', padding: '28px 20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Paiement & Wallet</h1>
        {wallet && (
          <div style={{
            background: 'rgba(110,86,207,0.12)',
            border: '1px solid rgba(110,86,207,0.28)',
            borderRadius: 14,
            padding: '10px 18px',
            textAlign: 'right'
          }}>
            <div style={{ fontSize: 11, color: 'rgb(var(--muted))', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
              Solde disponible
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#a78bfa', letterSpacing: '-0.02em' }}>
              {parseFloat(wallet.balance).toFixed(2)} €
            </div>
          </div>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`alert ${message.type === 'error' ? 'alert-error' : ''}`} style={{
          marginBottom: 16,
          ...(message.type !== 'error' ? {
            background: 'rgba(110,86,207,0.12)',
            borderColor: 'rgba(110,86,207,0.28)',
            color: '#a78bfa'
          } : {})
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            className="btn"
            onClick={() => setActiveTab(tab.key)}
            style={{
              fontSize: 13,
              padding: '7px 14px',
              ...(activeTab === tab.key ? {
                background: 'rgba(110,86,207,0.18)',
                borderColor: 'rgba(110,86,207,0.4)',
                color: '#a78bfa'
              } : {})
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Recharger ── */}
      {activeTab === 'wallet' && (
        <div className="section-card">
          <div className="section-head">
            <h2 className="section-title">Recharger mon wallet</h2>
          </div>

          <p className="muted" style={{ marginBottom: 18 }}>Choisissez un montant</p>

          {/* Montants */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
            {TOPUP_AMOUNTS.map(amount => (
              <button
                key={amount}
                type="button"
                className="btn"
                onClick={() => setSelectedAmount(amount)}
                style={{
                  padding: '14px 0',
                  fontSize: 16,
                  fontWeight: 700,
                  ...(selectedAmount === amount ? {
                    background: 'rgba(110,86,207,0.22)',
                    borderColor: '#6E56CF',
                    color: '#a78bfa'
                  } : {})
                }}
              >
                {amount} €
              </button>
            ))}
          </div>

          {/* Séparateur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
            <span className="muted">Choisissez votre mode de paiement</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
          </div>

          {/* PayPal */}
          <div style={{ marginBottom: 20 }}>
            <p className="muted" style={{ fontSize: 12, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>PayPal</p>
            <div style={{
              padding: '16px',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.07)',
              background: 'rgba(255,255,255,0.03)',
              overflow: 'hidden'
            }}>
              <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: 'EUR' }}>
                <PayPalButtons
                  style={{ layout: 'horizontal', color: 'gold', shape: 'pill', label: 'pay', height: 40, tagline: false }}
                  createOrder={async () => {
                    const res = await axios.post(`${API}/wallet/topup/paypal/create`, { amount: selectedAmount }, { headers });
                    return res.data.orderId;
                  }}
                  onApprove={async (data) => {
                    setLoading(true);
                    try {
                      await axios.post(`${API}/wallet/topup/paypal/capture`, { orderId: data.orderID, amount: selectedAmount }, { headers });
                      showMessage(`✅ ${selectedAmount} € ajoutés à votre wallet !`);
                      fetchAll();
                    } catch {
                      showMessage('Erreur lors de la capture PayPal', 'error');
                    }
                    setLoading(false);
                  }}
                  onError={() => showMessage('Erreur PayPal', 'error')}
                />
              </PayPalScriptProvider>
            </div>
          </div>

          {/* CB sauvegardée */}
          <div style={{ marginBottom: 20 }}>
            <p className="muted" style={{ marginBottom: 10 }}>Carte bancaire</p>
            {paymentMethods.filter(m => m.type === 'card').length > 0 ? (
              paymentMethods.filter(m => m.type === 'card').map(method => (
                <div key={method.id} className="ticket-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 14 }}>💳 {method.label}</span>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await axios.post(`${API}/wallet/topup/stripe`, { amount: selectedAmount, payment_method_id: method.provider_payment_method_id }, { headers });
                        showMessage(`✅ ${selectedAmount} € ajoutés à votre wallet !`);
                        fetchAll();
                      } catch {
                        showMessage('Erreur paiement', 'error');
                      }
                      setLoading(false);
                    }}
                  >
                    {loading ? '…' : `Payer ${selectedAmount} €`}
                  </button>
                </div>
              ))
            ) : (
              <p className="muted" style={{ fontStyle: 'italic' }}>
                Aucune carte sauvegardée — ajoutez-en une dans "Mes moyens de paiement".
              </p>
            )}
          </div>

          {/* Auto-recharge */}
          {autoTopup && (
            <div style={{
              marginTop: 8,
              padding: '14px 16px',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.03)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{ fontWeight: 600, margin: '0 0 3px', fontSize: 14 }}>Recharge automatique</p>
                <p className="muted" style={{ margin: 0 }}>
                  Recharge de {autoTopup.topup_amount} € si solde &lt; {autoTopup.threshold} €
                </p>
              </div>
              <button
                type="button"
                className={`btn ${autoTopup.enabled ? 'btn-primary' : ''}`}
                onClick={handleAutoTopupToggle}
                style={{ fontWeight: 700, fontSize: 12, padding: '6px 14px', borderRadius: 20 }}
              >
                {autoTopup.enabled ? 'ON' : 'OFF'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Mes moyens de paiement ── */}
      {activeTab === 'methods' && (
        <div className="section-card">
          <div className="section-head">
            <h2 className="section-title">Mes moyens de paiement</h2>
          </div>

          {paymentMethods.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💳</div>
              <div className="empty-title">Aucun moyen de paiement</div>
              <div className="empty-sub">Ajoutez une carte ou liez PayPal ci-dessous</div>
            </div>
          ) : (
            <div className="stack" style={{ marginBottom: 20 }}>
              {paymentMethods.map(method => (
                <div key={method.id} className="ticket-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{getMethodIcon(method.type)}</span>
                    <div>
                      <p style={{ fontWeight: 600, margin: '0 0 2px', fontSize: 14 }}>{method.label}</p>
                      <p className="muted" style={{ margin: 0, textTransform: 'capitalize' }}>{method.type}</p>
                    </div>
                    {method.is_default && (
                      <span className="badge" style={{ background: 'rgba(110,86,207,0.15)', borderColor: 'rgba(110,86,207,0.3)', color: '#a78bfa', fontSize: 11 }}>
                        Par défaut
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!method.is_default && (
                      <button type="button" className="btn" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => handleSetDefault(method.id)}>
                        Définir par défaut
                      </button>
                    )}
                    <button type="button" className="btn btn-danger" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => handleDeleteMethod(method.id)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 18 }}>
            <p className="muted" style={{ marginBottom: 12 }}>Ajouter un moyen de paiement</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn" onClick={() => setShowCardModal(true)}>
                💳 Ajouter une carte
              </button>
              <button type="button" className="btn" onClick={() => showMessage('Lier PayPal — bientôt disponible', 'error')}>
                🅿️ Lier PayPal
              </button>
              <button type="button" className="btn" onClick={() => showMessage('Paylib — bientôt disponible', 'error')}>
                📱 Lier Paylib
              </button>
            </div>
          </div>
          {showCardModal && (
            <SetupCardModal onSuccess={() => { setShowCardModal(false); fetchAll(); }} />
          )}
        </div>
      )}

      {/* ── Tab: Historique ── */}
      {activeTab === 'history' && (
        <div className="section-card">
          <div className="section-head">
            <h2 className="section-title">Historique des transactions</h2>
          </div>

          {transactions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🧾</div>
              <div className="empty-title">Aucune transaction</div>
              <div className="empty-sub">Votre historique apparaîtra ici après votre première recharge</div>
            </div>
          ) : (
            <div>
              {transactions.map((tx, i) => (
                <div key={tx.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 0',
                  borderBottom: i < transactions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 20 }}>
                      {tx.type === 'topup' ? '⬆️' : tx.type === 'refund' ? '↩️' : '⬇️'}
                    </span>
                    <div>
                      <p style={{ fontWeight: 500, margin: '0 0 2px', fontSize: 14 }}>{tx.description}</p>
                      <p className="muted" style={{ margin: 0 }}>
                        {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: tx.type === 'topup' || tx.type === 'refund' ? '#4ade80' : '#f87171'
                    }}>
                      {tx.type === 'topup' || tx.type === 'refund' ? '+' : '-'}{parseFloat(tx.amount).toFixed(2)} €
                    </div>
                    <div className="muted">Solde : {parseFloat(tx.balance_after).toFixed(2)} €</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
    </AppShell>
  );
}
